/* /app/negociation — module Négociation
 * MVP : liste + éditeur d'une négociation avec composantes mineures/majeures
 * (curseur d'élasticité 1-10), compatibilité des valeurs, lien Pipeline deals,
 * score de tension calculé, lecture IA NOAM.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import AiText from "@/components/AiText";
import { useTranslation } from "react-i18next";
import AnnuaireEntitySelector from "@/components/AnnuaireEntitySelector";
import { PersonaAction } from "@/lib/persona";
import DocumentAttachZone from "@/components/DocumentAttachZone";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import { DoctrineTestButton } from "@/pages/saas/V15Components";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import ProactiveWhisper from "@/components/ProactiveWhisper";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import {
  Plus, X, Trash2, Loader2, Handshake, ChevronRight, ChevronLeft, Sparkles,
  AlertTriangle, Link as LinkIcon, FileText,
} from "lucide-react";

const DIMENSIONS = ["time", "risk", "control", "trust", "prestige"];
const COMPAT = ["compatible", "divergent", "critical", "non_negotiable"];

export default function Negociation() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/negociations");
      setItems(r.data.items || []);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // V23.15.21 — Prefill par le Copilote IA (sessionStorage "noam_prefill_negociation")
  // Champs whitelisted : title, parties, context, objectif
  const [prefillNego, setPrefillNego] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("noam_prefill_negociation");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      sessionStorage.removeItem("noam_prefill_negociation");
      const payload = parsed && typeof parsed === "object" ? (parsed.data || parsed) : null;
      if (payload && typeof payload === "object") {
        setPrefillNego({
          title: typeof payload.title === "string" ? payload.title : "",
          parties: Array.isArray(payload.parties)
            ? payload.parties.join(", ")
            : (typeof payload.parties === "string" ? payload.parties : ""),
          context: typeof payload.context === "string" ? payload.context : "",
          objectif: typeof payload.objectif === "string" ? payload.objectif : "",
        });
        setCreating(true);
      }
    } catch {
      // ignore corrupted prefill
    }
  }, []);

  if (activeId) {
    return <NegotiationDetail id={activeId} onBack={() => { setActiveId(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="negociation-root">
      <ProactiveWhisper module="negociation" />
      {/* V23.15.36 — Doctrine 3Q · négociation la plus tendue */}
      <ThreeQPageHeader endpoint="/workspace/negociation/top-priority" />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[#378ADD] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5">
            <Handshake className="w-3 h-3" /> {t("nego.overline", { defaultValue: "Tactique" })}
          </div>
          <h1 className="noam-display text-3xl font-bold mt-1">{t("nego.title", { defaultValue: "Négociation" })}</h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
            {t("nego.subtitle", { defaultValue: "Transformez une négociation en système structuré de variables : composantes, élasticité, compatibilité des valeurs." })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ModuleHelpButton moduleKey="negociation" />
          <button onClick={() => setCreating(true)} className="btn-primary" data-testid="nego-new-btn">
            <Plus className="w-3.5 h-3.5" /> {t("nego.new", { defaultValue: "Nouvelle négociation" })}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center">
          <Handshake className="w-8 h-8 text-[#378ADD] mx-auto opacity-60" />
          <div className="noam-display text-lg mt-3">{t("nego.empty_title", { defaultValue: "Aucune négociation" })}</div>
          <p className="text-zinc-400 text-sm mt-1 max-w-md mx-auto">
            {t("nego.empty_desc", { defaultValue: "Démarrez avec un titre, les parties prenantes et le contexte. Vous structurerez les variables ensuite." })}
          </p>
          <button onClick={() => setCreating(true)} className="btn-primary mt-4" data-testid="nego-empty-cta">
            <Plus className="w-3.5 h-3.5" /> {t("nego.new", { defaultValue: "Nouvelle négociation" })}
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((n) => (
            <div key={n.id} className="relative">
              <button onClick={() => setActiveId(n.id)}
                className="noam-card p-5 text-left hover:border-[#378ADD]/50 transition-colors w-full"
                data-testid={`nego-card-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="noam-display text-base font-semibold truncate">{n.title}</div>
                  <TensionBadge t={n.tension} />
                </div>
                <p className="text-zinc-500 text-xs mt-1.5 line-clamp-2">{n.context || n.objectif || "—"}</p>
                <div className="flex items-center gap-3 mt-3 text-[11px] text-zinc-500">
                  <span>{(n.parties || []).length} {t("nego.parties_short", { defaultValue: "parties" })}</span>
                  <span>·</span>
                  <span>{(n.components || []).length} {t("nego.components_short", { defaultValue: "composantes" })}</span>
                  {n.deal_id && (<><span>·</span><span className="text-[#1D9E75] inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> deal</span></>)}
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 h-7" aria-hidden />
              </button>
              <div className="absolute bottom-3 left-5 right-5">
                <DoctrineTestButton
                  sourceModule="negociation"
                  sourceId={n.id}
                  label={n.title}
                  getText={() => `Négociation « ${n.title} ». Objectif : ${n.objectif || "non défini"}. Contexte : ${n.context || "—"}. Parties : ${(n.parties || []).map((p) => p.name || p).join(", ") || "?"}. Composantes : ${(n.components || []).map((c) => c.label || c).join(", ") || "—"}.`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <NewNegoModal onClose={() => { setCreating(false); setPrefillNego(null); }} onCreated={(id) => { setCreating(false); setPrefillNego(null); setActiveId(id); }} prefill={prefillNego} />
      )}
    </div>
  );
}

function TensionBadge({ t }) {
  if (!t) return null;
  const color = t.level === "high" ? "#E24B4A" : t.level === "medium" ? "#EF9F27" : "#1D9E75";
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded font-bold whitespace-nowrap"
      style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
      data-testid="nego-tension-badge">
      {t.score}/100
    </span>
  );
}

function NewNegoModal({ onClose, onCreated, prefill = null }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(prefill?.title || "");
  const [parties, setParties] = useState(prefill?.parties || "");
  const [context, setContext] = useState(prefill?.context || "");
  const [objectif, setObjectif] = useState(prefill?.objectif || "");
  const [deals, setDeals] = useState([]);
  const [dealId, setDealId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/workspace/origination/pipeline").then((r) => {
      const all = Object.values(r.data.by_step || {}).flat();
      setDeals(all);
    }).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/workspace/negociations", {
        title: title.trim(),
        parties: parties.split(",").map((p) => p.trim()).filter(Boolean),
        context, objectif,
        deal_id: dealId || null,
      });
      onCreated(r.data.id);
    } catch (e) {
      notify.error(fmtError(e));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="nego-new-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="noam-card p-6 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <div className="noam-display text-lg font-semibold">{t("nego.new", { defaultValue: "Nouvelle négociation" })}</div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {prefill && (
          <div
            className="px-3 py-2 rounded-md bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-[11px] text-[#EF9F27] inline-flex items-start gap-2"
            data-testid="nego-modal-prefill-banner"
          >
            <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{"Pré-rempli par l'IA Copilote — vérifiez et ajustez avant de créer."}</span>
          </div>
        )}
        <input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2}
          placeholder={t("nego.field_title", { defaultValue: "Titre" })}
          className="noam-input" data-testid="nego-modal-title" />
        <input value={parties} onChange={(e) => setParties(e.target.value)}
          placeholder={t("nego.field_parties", { defaultValue: "Parties prenantes (séparées par des virgules)" })}
          className="noam-input" data-testid="nego-modal-parties" />
        <textarea value={context} onChange={(e) => setContext(e.target.value)} rows={2}
          placeholder={t("nego.field_context", { defaultValue: "Contexte" })}
          className="noam-input resize-none" data-testid="nego-modal-context" />
        <textarea value={objectif} onChange={(e) => setObjectif(e.target.value)} rows={2}
          placeholder={t("nego.field_objectif", { defaultValue: "Objectif visé" })}
          className="noam-input resize-none" data-testid="nego-modal-objectif" />
        {deals.length > 0 && (
          <select value={dealId} onChange={(e) => setDealId(e.target.value)}
            className="noam-input" data-testid="nego-modal-deal">
            <option value="">{t("nego.no_deal", { defaultValue: "Aucun deal lié" })}</option>
            {deals.map((d) => (<option key={d.id} value={d.id}>{d.titre}</option>))}
          </select>
        )}
        <button type="submit" disabled={busy || !title.trim()} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="nego-modal-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {t("common.create", { defaultValue: "Créer" })}
        </button>
      </form>
    </div>
  );
}

function ScalHeader({ neg, setNeg, onSave }) {
  const { t } = useTranslation();
  return (
    <div className="noam-card p-4 space-y-3" data-testid="nego-scal-header">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#EF9F27] font-bold">S.C.A.L.</span>
        <span className="text-[11px] text-zinc-500">
          {t("nego.scal_help", { defaultValue: "Sujet · Composante · Ambition · Limite (cadrage CeO)" })}
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {t("nego.subject", { defaultValue: "Sujet — « Je veux… »" })}
          </div>
          <input
            value={neg.subject || ""}
            onChange={(e) => setNeg({ ...neg, subject: e.target.value })}
            onBlur={() => onSave({ subject: neg.subject || "" })}
            placeholder={t("nego.subject_ph", { defaultValue: "Je veux …" })}
            className="noam-input w-full"
            data-testid="nego-scal-subject"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {t("nego.amc", { defaultValue: "A.M.C. — Aux Meilleures Conditions" })}
          </div>
          <input
            value={neg.amc || ""}
            onChange={(e) => setNeg({ ...neg, amc: e.target.value })}
            onBlur={() => onSave({ amc: neg.amc || "" })}
            placeholder={t("nego.amc_ph", { defaultValue: "Conditions optimales visées (prix · timing · garanties · termes)…" })}
            className="noam-input w-full"
            data-testid="nego-scal-amc"
          />
        </label>
      </div>
    </div>
  );
}

function NegotiationDetail({ id, onBack }) {
  const { t } = useTranslation();
  const [neg, setNeg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/workspace/negociations/${id}`); setNeg(r.data); }
    catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const save = async (patch) => {
    try { const r = await api.patch(`/workspace/negociations/${id}`, patch); setNeg(r.data); }
    catch (e) { notify.error(fmtError(e)); }
  };
  const runAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/workspace/negociations/${id}/ai-analysis`);
      setNeg((n) => ({ ...n, ai_reading: r.data.ai_reading, tension: r.data.tension }));
      notify.success(t("nego.ai_done", { defaultValue: "Analyse IA prête." }));
    } catch (e) { notify.error(fmtError(e)); }
    setAiBusy(false);
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/workspace/negociations/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `negociation-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
  };

  if (loading || !neg) {
    return <div className="text-zinc-400 text-sm flex items-center gap-2" data-testid="nego-detail-loading"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>;
  }

  return (
    <div className="space-y-6" data-testid="nego-detail-root">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white" data-testid="nego-detail-back">
          <ChevronLeft className="w-3.5 h-3.5" /> {t("common.back", { defaultValue: "Retour" })}
        </button>
        <PersonaAction action="export_pdf">
          <button onClick={downloadPdf} className="btn-secondary text-xs" data-testid="nego-pdf-btn">
            <FileText className="w-3 h-3" /> {t("nego.pdf", { defaultValue: "Export PDF" })}
          </button>
        </PersonaAction>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <input value={neg.title} onChange={(e) => setNeg({ ...neg, title: e.target.value })}
            onBlur={() => save({ title: neg.title })}
            className="bg-transparent border-0 noam-display text-2xl font-bold w-full focus:outline-none"
            data-testid="nego-detail-title-input" />
          <p className="text-zinc-500 text-xs mt-1">{t("nego.parties_label", { defaultValue: "Parties" })} : {(neg.parties || []).join(", ") || "—"}</p>
        </div>
        <TensionPanel tension={neg.tension} onAI={runAI} aiBusy={aiBusy} />
      </div>

      <ScalHeader neg={neg} setNeg={setNeg} onSave={save} />

      <div className="noam-card p-4" data-testid="nego-entities-card">
        <AnnuaireEntitySelector
          value={neg.annuaire_entity_ids || []}
          onChange={(ids) => { setNeg({ ...neg, annuaire_entity_ids: ids }); save({ annuaire_entity_ids: ids }); }}
          testid="nego-entity-selector"
          label={t("nego.entities_label", { defaultValue: "Parties prenantes — Annuaire" })}
          placeholder={t("nego.entities_ph", { defaultValue: "Rechercher contact, holding, fonds…" })}
        />
      </div>

      <ComponentsEditor neg={neg} onSave={save} />
      <ValuesEditor neg={neg} onSave={save} />

      <DocumentAttachZone module="negociation" entityId={neg.id} entityLabel={neg.title} />

      <HybridResearchPanel
        contextModule="generic"
        initialTopic={neg.title || ""}
        defaultMode="hybrid"
        title="Analyser la contrepartie face au marché"
        testid="nego-hybrid-research"
      />

      {neg.ai_reading && (
        <div className="noam-card p-5">
          <div className="text-[#378ADD] text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {t("nego.ai_reading", { defaultValue: "Lecture IA" })}
          </div>
          <pre className="whitespace-pre-wrap text-sm text-zinc-200 mt-2 font-sans"><AiText>{neg.ai_reading}</AiText></pre>
        </div>
      )}
    </div>
  );
}

function TensionPanel({ tension, onAI, aiBusy }) {
  const { t } = useTranslation();
  if (!tension) return null;
  const c = tension.level === "high" ? "#E24B4A" : tension.level === "medium" ? "#EF9F27" : "#1D9E75";
  const flexColor = tension.flex_level === "high" ? "#1D9E75" : tension.flex_level === "medium" ? "#EF9F27" : "#E24B4A";
  return (
    <div className="noam-card p-4 min-w-[240px] text-center" data-testid="nego-tension-panel">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("nego.tension", { defaultValue: "Tension" })}</div>
      <div className="text-3xl font-bold mt-1" style={{ color: c }} data-testid="nego-tension-score">{tension.score}</div>
      <div className="text-[10px] text-zinc-500 mt-1">
        {tension.rigid_majors} {t("nego.rigid_majors", { defaultValue: "majeurs rigides" })} · {tension.incompat_critical + tension.incompat_non_negotiable} {t("nego.incompat", { defaultValue: "incompat." })}
      </div>
      <div className="mt-3 pt-3 border-t border-white/5" data-testid="nego-flex-panel">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("nego.flexibility", { defaultValue: "Flexibilité" })}</div>
        <div className="text-lg font-semibold mt-0.5" style={{ color: flexColor }} data-testid="nego-flex-ratio">
          {tension.flex_ratio ?? 0}%
        </div>
        <div className="text-[10px] text-zinc-500">
          {tension.n_majors || 0} maj. · {tension.n_minors || 0} min.
        </div>
      </div>
      <button onClick={onAI} disabled={aiBusy} className="btn-primary mt-3 w-full justify-center text-xs disabled:opacity-50" data-testid="nego-ai-btn">
        {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        {t("nego.ai_btn", { defaultValue: "Lecture IA" })}
      </button>
    </div>
  );
}

function ComponentsEditor({ neg, onSave }) {
  const { t } = useTranslation();
  const [items, setItems] = useState(neg.components || []);
  const updateItem = (idx, patch) => {
    const next = items.map((c, i) => i === idx ? { ...c, ...patch } : c);
    setItems(next);
  };
  const commit = () => onSave({ components: items });
  const add = (severity) => {
    setItems([...items, { name: t("nego.new_component", { defaultValue: "Nouvelle composante" }), severity, elasticity: 50, notes: "", ambition: "", limit: "" }]);
  };
  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    onSave({ components: next });
  };
  const majors = items.map((c, i) => ({ ...c, _idx: i })).filter((c) => c.severity === "major");
  const minors = items.map((c, i) => ({ ...c, _idx: i })).filter((c) => c.severity !== "major");
  const Row = ({ c }) => (
    <div className="py-2 border-b border-white/5 last:border-0 space-y-1.5" data-testid={`nego-comp-row-${c._idx}`}>
      <div className="flex items-center gap-2">
        <input value={c.name} onChange={(e) => updateItem(c._idx, { name: e.target.value })}
          onBlur={commit}
          className="noam-input flex-1 text-sm"
          data-testid={`nego-comp-name-${c._idx}`} />
        <input type="range" min="1" max="100" value={c.elasticity || 50}
          onChange={(e) => updateItem(c._idx, { elasticity: parseInt(e.target.value) })}
          onMouseUp={commit} onTouchEnd={commit}
          className="w-24"
          data-testid={`nego-comp-elasticity-${c._idx}`} />
        <span className="text-xs text-zinc-400 w-12 text-right">{c.elasticity || 50}/100</span>
        <button onClick={() => remove(c._idx)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`nego-comp-del-${c._idx}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5 pl-1">
        <input
          value={c.ambition || ""}
          onChange={(e) => updateItem(c._idx, { ambition: e.target.value })}
          onBlur={commit}
          placeholder={t("nego.ambition_ph", { defaultValue: "Ambition (A) — ce qu'on vise" })}
          className="noam-input text-[11px] py-1"
          data-testid={`nego-comp-ambition-${c._idx}`}
        />
        <input
          value={c.limit || ""}
          onChange={(e) => updateItem(c._idx, { limit: e.target.value })}
          onBlur={commit}
          placeholder={c.severity === "major"
            ? t("nego.limit_major_ph", { defaultValue: "Limite (L) — point de rupture" })
            : t("nego.limit_minor_ph", { defaultValue: "Limite (∅ mineures)" })}
          className={`noam-input text-[11px] py-1 ${c.severity !== "major" ? "opacity-60" : ""}`}
          data-testid={`nego-comp-limit-${c._idx}`}
        />
      </div>
    </div>
  );
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="noam-card p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="noam-display text-base font-semibold">{t("nego.majors_title", { defaultValue: "Composantes majeures" })}</div>
          <button onClick={() => add("major")} className="text-xs text-[#378ADD] hover:underline" data-testid="nego-add-major">+ {t("common.add", { defaultValue: "Ajouter" })}</button>
        </div>
        <p className="text-[11px] text-zinc-500 mb-2">{t("nego.majors_help", { defaultValue: "Contrôle · capital · garanties · gouvernance · juridiction · temporalité · pouvoir." })}</p>
        {majors.length === 0 ? <div className="text-zinc-500 text-xs italic">{t("nego.empty_majors", { defaultValue: "Aucune composante majeure." })}</div> : majors.map((c) => <Row key={c._idx} c={c} />)}
      </div>
      <div className="noam-card p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="noam-display text-base font-semibold">{t("nego.minors_title", { defaultValue: "Composantes mineures" })}</div>
          <button onClick={() => add("minor")} className="text-xs text-[#378ADD] hover:underline" data-testid="nego-add-minor">+ {t("common.add", { defaultValue: "Ajouter" })}</button>
        </div>
        <p className="text-[11px] text-zinc-500 mb-2">{t("nego.minors_help", { defaultValue: "Confort · détails · wording · options accessoires." })}</p>
        {minors.length === 0 ? <div className="text-zinc-500 text-xs italic">{t("nego.empty_minors", { defaultValue: "Aucune composante mineure." })}</div> : minors.map((c) => <Row key={c._idx} c={c} />)}
      </div>
    </div>
  );
}

function ValuesEditor({ neg, onSave }) {
  const { t } = useTranslation();
  const [items, setItems] = useState(neg.values_compat || []);
  const parties = neg.parties || [];
  const update = (idx, patch) => {
    const next = items.map((v, i) => i === idx ? { ...v, ...patch } : v);
    setItems(next);
  };
  const commit = () => onSave({ values_compat: items });
  const add = () => {
    if (parties.length < 2) { notify.error(t("nego.need_2_parties", { defaultValue: "Ajoutez au moins 2 parties prenantes." })); return; }
    setItems([...items, { party_a: parties[0], party_b: parties[1], dimension: "time", status: "compatible", notes: "" }]);
  };
  const remove = (idx) => { const next = items.filter((_, i) => i !== idx); setItems(next); onSave({ values_compat: next }); };
  return (
    <div className="noam-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="noam-display text-base font-semibold">{t("nego.values_title", { defaultValue: "Compatibilité des valeurs" })}</div>
          <p className="text-[11px] text-zinc-500">{t("nego.values_help", { defaultValue: "5 dimensions : rapport au temps, risque, contrôle, confiance, prestige." })}</p>
        </div>
        <button onClick={add} className="text-xs text-[#378ADD] hover:underline" data-testid="nego-add-value">+ {t("common.add", { defaultValue: "Ajouter" })}</button>
      </div>
      {items.length === 0 ? (
        <div className="text-zinc-500 text-xs italic">{t("nego.empty_values", { defaultValue: "Aucune compatibilité définie." })}</div>
      ) : (
        <div className="space-y-2">
          {items.map((v, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center py-1.5" data-testid={`nego-value-row-${idx}`}>
              <select value={v.party_a} onChange={(e) => update(idx, { party_a: e.target.value })} onBlur={commit} className="noam-input col-span-3 text-xs">
                {parties.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <span className="text-center text-zinc-500 text-xs">↔</span>
              <select value={v.party_b} onChange={(e) => update(idx, { party_b: e.target.value })} onBlur={commit} className="noam-input col-span-3 text-xs">
                {parties.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={v.dimension} onChange={(e) => update(idx, { dimension: e.target.value })} onBlur={commit} className="noam-input col-span-2 text-xs">
                {DIMENSIONS.map((d) => <option key={d} value={d}>{t(`nego.dim_${d}`, { defaultValue: d })}</option>)}
              </select>
              <select value={v.status} onChange={(e) => update(idx, { status: e.target.value })} onBlur={commit}
                className="noam-input col-span-2 text-xs"
                style={{ borderColor: v.status === "critical" || v.status === "non_negotiable" ? "#E24B4A" : v.status === "divergent" ? "#EF9F27" : "#1D9E75" }}>
                {COMPAT.map((s) => <option key={s} value={s}>{t(`nego.compat_${s}`, { defaultValue: s })}</option>)}
              </select>
              <button onClick={() => remove(idx)} className="text-zinc-500 hover:text-red-400 col-span-1 text-right">
                <Trash2 className="w-3.5 h-3.5 inline" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
